import { Injectable } from "@angular/core";
import { ToastrService } from "ngx-toastr";
import { catchError, lastValueFrom } from "rxjs";
import { SpinnerService } from "../../../components/spinner/spinner.service";
import { ConfirmService } from "../../../components/confirm-dialog/confirm-dialog.service";
import { ProductDto, ProductStatusEnum, UserDto, UserStatusEnum } from "../../../dto";
import { HttpProductService } from "../../../services/product/http-product.service";

@Injectable({
	providedIn: 'root'
})
export class ProductActionService {

	constructor(
		private httpProductService: HttpProductService,
		private toastr: ToastrService,
		private spinnerService: SpinnerService,
		private confirmService: ConfirmService
	) { }

	async disableProduct(input: ProductDto): Promise<boolean> {
		const confirmed = await this.confirmService.show({
			title: 'Disable Product',
			text: `Are you sure you wish to disable: ${input.name}?`,
			subText: `Packaging will no longer be available for ${input.name}.`,
			yesText: 'Disable',
			noText: 'Cancel'
		});

		if (!confirmed) return false;

		const payload = { ...input, status: ProductStatusEnum.Disabled };

		this.spinnerService.show('Disabling product...');
		try {
			const result = await lastValueFrom(
				this.httpProductService.productAction(payload, 'disable').pipe(
					catchError(err => {
						console.error('Error disabling product', err);
						this.toastr.error('Failed to disable product: ' + (err.error?.error ?? err.message));
						throw err;
					})
				)
			);

			if (result.success) {
				this.toastr.info('Product disabled successfully');
				return true;
			} else {
				this.toastr.error('Failed to disable product');
				return false;
			}
		} finally {
			this.spinnerService.hide();
		}
	}

	async enableProduct(input: ProductDto): Promise<boolean> {
		const confirmed = await this.confirmService.show({
			title: 'Enable Product',
			text: `Are you sure you wish to enable: ${input.name}?`,
			subText: `Packaging will be available for ${input.name}.`,
			yesText: 'Enable',
			noText: 'Cancel'
		});

		if (!confirmed) return false;

		const payload = { ...input, status: ProductStatusEnum.Active };

		this.spinnerService.show('Enabling product...');
		try {
			const result = await lastValueFrom(
				this.httpProductService.productAction(payload, 'enable').pipe(
					catchError(err => {
						console.error('Error enabling product', err);
						this.toastr.error('Failed to enable product: ' + (err.error?.error ?? err.message));
						throw err;
					})
				)
			);

			if (result.success) {
				this.toastr.info('Product enabled successfully');
				return true;
			} else {
				this.toastr.error('Failed to enable product');
				return false;
			}
		} finally {
			this.spinnerService.hide();
		}
	}

}